import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import SystemService from "@/services/SystemService";
import ToastMe from "@/components/ui/ToastMe";
import { cn } from "@/lib/utils";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
  } catch (err) {
    return String(value);
  }
};

const InfoRow = ({ label, value, monospace }) => (
  <div className="space-y-1">
    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
    <dd
      className={cn(
        "text-sm text-slate-900 break-words",
        monospace && "font-mono text-xs sm:text-sm bg-slate-100 px-2 py-1 rounded"
      )}
    >
      {value ?? "—"}
    </dd>
  </div>
);

const LicenseSettingsPage = ({ pageTitle = "License" }) => {
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [accessKey, setAccessKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemService.fetchAdminLicenseStatus();
      setSnapshot(data);
      setAccessKey(data?.licenseKey || "");
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Unable to load license information.";
      setError(message);
      ToastMe(message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncAccessKey = useCallback(async (event) => {
    event.preventDefault();
    if (!accessKey.trim()) {
      ToastMe("Access key is required.", "error");
      return;
    }

    setSavingKey(true);
    try {
      const response = await SystemService.syncLicense({ accessKey: accessKey.trim() });
      ToastMe(response?.message || "Access key synced successfully.", "success");
      await loadSnapshot();
    } catch (err) {
      ToastMe(
        err?.response?.data?.message || err?.message || "Unable to sync access key.",
        "error"
      );
    } finally {
      setSavingKey(false);
    }
  }, [accessKey, loadSnapshot]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const statusBadge = useMemo(() => {
    if (!snapshot) return null;
    const isActive = snapshot.status === "ok" && snapshot.cacheValid && !snapshot.locked;
    const base = "px-3 py-1 rounded-full text-xs font-semibold border";
    const badgeClass = isActive
      ? `${base} bg-emerald-100 text-emerald-700 border-emerald-200`
      : `${base} bg-amber-100 text-amber-700 border-amber-200`;
    return <span className={badgeClass}>{isActive ? "Active" : "Activation Required"}</span>;
  }, [snapshot]);

  const seedAutoLabel = useMemo(() => {
    if (!snapshot) return "—";
    return snapshot.runSeedOnActivation ? "Enabled" : "Disabled";
  }, [snapshot]);

  const handleCopy = useCallback((value) => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => ToastMe("License key copied to clipboard.", "success"))
      .catch(() => ToastMe("Unable to copy license key.", "error"));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">{pageTitle}</h1>
        <button
          type="button"
          onClick={loadSnapshot}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90 disabled:opacity-60"
          disabled={loading}
        >
          <i className="fa-solid fa-rotate-right" />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="db-card p-5 text-sm text-slate-600">Loading license information…</div>
      )}

      {error && !loading && (
        <div className="db-card border border-red-200 bg-red-50 p-5 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && snapshot && (
        <>
          <div className="db-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">License</h2>
                <p className="text-sm text-slate-500">
                  Visibility into activation details and enforcement mode from the Taban licensing server.
                </p>
              </div>
              {statusBadge}
            </div>

            <form className="mt-5 flex flex-col gap-3 sm:flex-row" onSubmit={handleSyncAccessKey}>
              <input
                type="text"
                value={accessKey}
                onChange={(event) => setAccessKey(event.target.value)}
                className="min-h-11 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
                placeholder="Enter Taban access key"
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={savingKey}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90 disabled:opacity-60"
              >
                <i className="fa-solid fa-key" />
                {savingKey ? "Syncing..." : "Sync Access Key"}
              </button>
            </form>

            <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="License Key" value={snapshot.licenseKey || "No access key stored"} monospace />
              <div className="flex items-end">
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-2 rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                  onClick={() => handleCopy(snapshot.licenseKey)}
                  disabled={!snapshot?.licenseKey}
                >
                  <i className="fa-regular fa-copy" />
                  Copy Key
                </button>
              </div>
              <InfoRow label="License Server URL" value={snapshot.serverUrl || "—"} />
              <InfoRow label="Validation Path" value={snapshot.validatePath || "—"} />
              <InfoRow label="Activation Source" value={snapshot.cacheFile || "—"} monospace />
              <InfoRow label="Enforcement Mode" value={snapshot.enforce ? "Required" : "Optional"} />
              <InfoRow label="Organization" value={snapshot.organization?.name || "—"} />
              <InfoRow label="System" value={snapshot.organization?.systemType || "—"} />
              <InfoRow label="Mongo Connected" value={snapshot.mongoConnected ? "Yes" : "No"} />
              <InfoRow label="Locked" value={snapshot.locked ? "Yes" : "No"} />
            </dl>

            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700">Activation Details</h3>
              <dl className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <InfoRow label="Organization ID" value={snapshot.activation?.tenantId || "—"} />
                <InfoRow label="Activation ID" value={snapshot.activation?.activationId || "—"} />
                <InfoRow label="Product" value={snapshot.licensing?.productName || "—"} />
                <InfoRow label="Plan" value={snapshot.licensing?.plan || "—"} />
                <InfoRow label="Access Type" value={snapshot.licensing?.accessType || "—"} />
                <InfoRow label="License Expires" value={formatDateTime(snapshot.activation?.expiresAt)} />
                <InfoRow label="Validated At" value={formatDateTime(snapshot.activation?.validatedAt)} />
                <InfoRow label="Last Verified" value={formatDateTime(snapshot.activation?.lastVerifiedAt)} />
              </dl>

              {snapshot.activationPayload && (
                <details className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <summary className="cursor-pointer font-semibold text-slate-600">
                    View cached server payload
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-slate-800">
                    {JSON.stringify(snapshot.activationPayload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>

          <div className="db-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Licensing Record</h2>
                <p className="text-sm text-slate-500">
                  Snapshot of the license stored in your SaaS licensing system.
                </p>
              </div>
              <span className="text-xs font-medium text-slate-500">
                {snapshot.licensing ? "Synced with SaaS licensing service" : "No record found"}
              </span>
            </div>

            {snapshot.licensing ? (
              <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <InfoRow label="Status" value={snapshot.licensing.status || "—"} />
                <InfoRow label="Product" value={snapshot.licensing.productName || "—"} />
                <InfoRow label="Plan" value={snapshot.licensing.plan || "—"} />
                <InfoRow label="Access Type" value={snapshot.licensing.accessType || "—"} />
                <InfoRow label="Trial Ends" value={formatDateTime(snapshot.licensing.trialEndsAt)} />
                <InfoRow label="License Expires" value={formatDateTime(snapshot.licensing.expiresAt)} />
                <InfoRow label="Trial Cleanup" value={formatDateTime(snapshot.licensing.trialCleanupAt)} />
                <InfoRow label="License Hint" value={snapshot.licensing.keyHint || "—"} />
                <InfoRow label="Message" value={snapshot.licensing.message || "—"} />
              </dl>
            ) : (
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                No licensing record found for the current company. Generate a license in your SaaS
                console, then activate this installation.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LicenseSettingsPage;
